import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  getPathWorkLogEdit,
  PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS
} from "../../../modules/WorkLogs/paths";
import { useFeatureFlags } from "../../../query/hooks/useFeatureFlags";
import { useMutationGetOrCreateWorkLog } from "../../../query/workLog/mutations/useMutationGetOrCreateWorkLog";
import { getPayerEngagementsWorkDefinitions } from "../../../services/payerEngagements/getPayerEngagementWorkDefinitions";

type Params = {
  payerId: string | null | undefined;
  payerPayeeEngagementId: string | null | undefined;
  skip?: boolean;
};

export const useWorkDefinitionRedirect = ({
  payerId,
  payerPayeeEngagementId,
  skip
}: Params) => {
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const checkedEngagementRef = useRef<string | null>(null);
  const queryFeatureFlags = useFeatureFlags();
  const [getOrCreateWorkLog, getOrCreateWorkLogMeta] =
    useMutationGetOrCreateWorkLog();

  const isWorkLogsListEnabled = queryFeatureFlags.data?.workLogsList;

  useEffect(() => {
    const handleEngagementSelection = async () => {
      if (!payerPayeeEngagementId || !payerId) {
        return;
      }

      const engagementKey = `${payerId}/${payerPayeeEngagementId}`;
      if (checkedEngagementRef.current === engagementKey) {
        return;
      }
      checkedEngagementRef.current = engagementKey;

      try {
        setIsLoading(true);

        const definitions = await getPayerEngagementsWorkDefinitions(
          {
            engagementId: payerPayeeEngagementId,
            payerId
          },
          {
            page: {
              size: 1
            }
          }
        );

        if (definitions?.length) {
          if (isWorkLogsListEnabled) {
            // New behavior: redirect to work logs list page with pre-selected engagement
            const params = new URLSearchParams({
              payerId,
              engagementId: payerPayeeEngagementId
            });
            history.push(
              `${PATH_WORK_LOGS_PAYEE_TAB_IN_PROGRESS}?${params.toString()}`
            );
          } else {
            // Old behavior: create work log and redirect to edit page
            await getOrCreateWorkLog(
              { payerPayeeEngagementId },
              {
                onSuccess: workLog => {
                  history.push(getPathWorkLogEdit(workLog.workLogId));
                }
              }
            );
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (
      !!payerId &&
      !!payerPayeeEngagementId &&
      !skip &&
      !queryFeatureFlags.isLoading &&
      getOrCreateWorkLogMeta.isIdle
    ) {
      handleEngagementSelection();
    }
  }, [
    getOrCreateWorkLog,
    getOrCreateWorkLogMeta.isIdle,
    history,
    isWorkLogsListEnabled,
    payerId,
    payerPayeeEngagementId,
    queryFeatureFlags.isLoading,
    skip
  ]);

  return { isLoading: isLoading || queryFeatureFlags.isLoading };
};
